// contains data for compare panel and compare checkboxes for each product
import * as log from 'loglevel';
import { createAction, createReducer } from '@reduxjs/toolkit';

import { getActiveListingPage } from './selectors';
import { getComparePopupProducts } from './listingPageComparePopup.reduck';
import {
  dispatchClickCompareButton, dispatchComparisonCheckClick, dispatchClearAllProducts, dispatchUncheckProduct
} from '../analytics/compareAnalytics';

// 1. Initial state
const initialState = {
  // contains products' data of products which were added to the compare panel
  // if array is empty, no product was added to compare panel
  products: {}
};

// 2. Basic actions creators
const addProductForSegment = createAction('listingPage/comparision/addProductForSegment');
const removeProductFromSegment = createAction('listingPage/comparision/removeProductFromSegment');
const removeAllProductsFromSegment = createAction('listingPage/comparision/removeAllProductsFromSegment');


// 3. Complex actions which call basic actions above
export function addProductAndTriggerAnalytics(productId) {
  return (dispatch, getState) => {
    const productSegmentId = Number(getActiveListingPage(getState()).pageAttributes.filterAttributes.segment_id[0]);

    if (Array.isArray(getState().listingPage.comparison.products[productSegmentId]) &&
      (getState().listingPage.comparison.products[productSegmentId].length >= 4 ||
      getState().listingPage.comparison.products[productSegmentId]
        .findIndex((product) => product.id === productId) !== -1)) {
      log.warn(`Product with id ${productId} is already in comparison list or 
        there are already 4 products in comparison list`);

      // Only Analytics is dispatched
      dispatch(dispatchComparisonCheckClick(productId, 'error', 'User Error Message'));

    } else {
      const productToAdd =
        getActiveListingPage(getState()).productList.find((product) => product.id === productId) ||
        // product is in sponsored products, if it is not in regular products
        getActiveListingPage(getState()).sponsoredProducts.items.find((product) => product.id === productId);

      // Analytics
      dispatch(dispatchComparisonCheckClick(productId, 'checked', null));

      dispatch(addProductForSegment({ productToAdd, productSegmentId }));
    }
  };
}

export function removeProductAndTriggerAnalytics(productId, GUIWhereTriggered = 'CompareCheckbox') {
  return (dispatch, getState) => {
    const productSegmentId = Number(getActiveListingPage(getState()).pageAttributes.filterAttributes.segment_id[0]);

    if (Array.isArray(getState().listingPage.comparison.products[productSegmentId]) &&
      getState().listingPage.comparison.products[productSegmentId]
        .findIndex((product) => product.id === productId) !== -1) {
      // Analytics
      if (GUIWhereTriggered === 'CompareCheckbox') {
        dispatch(dispatchComparisonCheckClick(productId, 'unchecked', null));
      }
      if (GUIWhereTriggered === 'ComparePanel' || GUIWhereTriggered === 'ComparePopup') {
        dispatch(dispatchUncheckProduct(productId));
      }

      dispatch(removeProductFromSegment({ productId, productSegmentId }));

    } else {
      log.warn(`Product with id ${productId} is not in comparison list for segment ${productSegmentId} so it can't be removed`);

      // Analytics
      if (GUIWhereTriggered === 'CompareCheckbox') {
        dispatch(dispatchComparisonCheckClick(productId, 'error', 'User Error Message'));
      }
    }
  };
}

export function removeAllProductsForSegmentAndTriggerAnalytics() {
  return (dispatch, getState) => {
    const productSegmentId = Number(getActiveListingPage(getState()).pageAttributes.filterAttributes.segment_id[0]);

    if (getState().listingPage.comparison.products[productSegmentId]) {

      dispatchClearAllProducts(getState().listingPage.comparison.products[productSegmentId].map((product) => product.id));
      dispatch(removeAllProductsFromSegment({ productSegmentId }));
    } else {
      log.warn(`No products array defined in 'listingPage.comparison.products' object for segment ${productSegmentId}.`);
    }
  };
}

export function showProductsComparision() {
  return (dispatch, getState) => {
    const currentPageSegmentId = Number(getActiveListingPage(getState()).pageAttributes.filterAttributes.segment_id[0]);

    const productsToCompare =
      Array.isArray(getState().listingPage.comparison.products[currentPageSegmentId]) &&
      getState().listingPage.comparison.products[currentPageSegmentId].length > 1
        ? getState().listingPage.comparison.products[currentPageSegmentId]
        : null;

    if (!productsToCompare) {
      log.warn(`No products to compare for segment ${currentPageSegmentId}`);
      return;
    }

    const productIds = productsToCompare.map((product) => product.id);

    // Analytics
    dispatch(dispatchClickCompareButton(productIds));
    dispatch(getComparePopupProducts(productIds));

  };
}

// 4. Reducer
/* eslint-disable no-param-reassign */
export default createReducer(initialState, {
  [addProductForSegment]: (stateDraft, action) => {
    const productSegmentId = action.payload.productSegmentId;

    if (stateDraft.products[productSegmentId]) {
      stateDraft.products[productSegmentId].push(action.payload.productToAdd);
    } else {
      stateDraft.products[productSegmentId] = [action.payload.productToAdd];
    }
  },
  [removeProductFromSegment]: (stateDraft, action) => {
    const productSegmentId = action.payload.productSegmentId;

    if (stateDraft.products[productSegmentId].length > 1) {
      const productToRemoveId = action.payload && action.payload.productId;

      stateDraft.products[productSegmentId] = stateDraft.products[productSegmentId].filter(
        (savedProduct) => savedProduct.id !== productToRemoveId
      );
    } else {
      // If there are no products, remove segment index from products collection.
      // We want to avoid empty segment array.
      delete stateDraft.products[productSegmentId];
    }
  },
  [removeAllProductsFromSegment]: (stateDraft, action) => {
    const productSegmentId = action.payload.productSegmentId;

    delete stateDraft.products[productSegmentId];
  }
});
